import {
  Patient,
  DocumentReference,
  Provenance,
  Practitioner,
  Organization,
  Binary,
  DiagnosticReport,
  Reference,
  Resource,
  RequestGroup,
} from '@medplum/fhirtypes'
import { MedplumClient, getReferenceString } from '@medplum/core'
import { formatHumanName } from '@medplum/core'
import { DeliveryExtension, DeliveryMethod } from './components/DeliveryMethodToggle'

function buildDeliveryExtension(
  method: DeliveryMethod,
  phoneNumber?: string,
): DeliveryExtension | null {
  if (!method) {
    return null
  }

  if (method === 'electronic') {
    return {
      url: 'https://www.behuman.health/fhir/StructureDefinition/requestgroup-deliveryOptions',
      extension: [
        {
          url: 'electronic',
          valueBoolean: true,
        },
      ],
    }
  }

  if (method === 'fax') {
    if (!phoneNumber) {
      throw new Error('Phone number is required when delivery method is fax.')
    }

    return {
      url: 'https://www.behuman.health/fhir/StructureDefinition/requestgroup-deliveryOptions',
      extension: [
        {
          url: 'method',
          valueContactPoint: {
            system: 'fax',
            value: phoneNumber,
          },
        },
      ],
    }
  }
  return null
}

function buildProviderNameExtension(
  providerName: string,
  reference: string | null = null,
): { url: string; valueReference: { display: string; reference?: string | null } } {
  const valueReference: { display: string; reference?: string | null } = reference
    ? { display: providerName, reference }
    : { display: providerName }

  return {
    url: 'https://www.behuman.health/fhir/StructureDefinition/requestgroup-performer',
    valueReference,
  }
}
function buildLabReferenceIdentifier(labId: string) {
  return [
    {
      type: {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
            code: 'ACSN',
            display: 'Accession ID',
          },
        ],
        text: 'Lab Reference ID',
      },
      value: labId,
    },
  ]
}

const createProvenanceResource = async (
  medplum: MedplumClient,
  docRefId: string,
  practitioner: Practitioner,
  organization: Organization,
  binary: Binary,
  targetResourceType: string,
) => {
  const provenanceInput: Provenance = {
    resourceType: 'Provenance',
    target: [
      {
        reference: `DocumentReference/${docRefId}`,
        display: targetResourceType, //'Progress Note'
      },
    ],
    recorded: new Date().toISOString(),
    agent: [
      {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
              code: 'performer',
              display: 'Performer',
            },
          ],
        },
        who: {
          reference: getReferenceString(practitioner),
          display: practitioner?.name
            ? formatHumanName(practitioner?.name[0])
            : 'Unknown Practitioner',
        },
        //  onBehalfOf: {
        //   reference: getReferenceString(organization),
        //   display: organization.name || 'Unknown Organization'
        // }
      },
      {
        type: {
          coding: [
            {
              system: 'https://www.behuman.health/provenance-participant-type',
              code: 'source',
              display: 'Source',
            },
          ],
        },
        who: {
          reference: getReferenceString(organization),
          display: organization.name || 'Unknown Organization',
        },
      },
    ],
    entity: [
      {
        role: 'source', // source input to the target
        what: { reference: getReferenceString(binary) },
      },
    ],
  }

  const provenance = await medplum.createResource<Provenance>(provenanceInput)
  return provenance
}
const getDynamicPayload = (docType: string) => {
  switch (docType) {
    case 'progressNote':
      return {
        type: {
          coding: [
            {
              system: 'http://loinc.org',
              code: '11506-3',
              display: 'Provider-unspecified Progress note',
            },
            {
              system: 'https://behuman.health/document-type',
              code: 'ProgressNote',
              display: 'Progress Note',
            },
          ],
          text: 'Progress Note',
        },
      }
    case 'HistoryPhysical':
      return {
        type: {
          coding: [
            {
              code: '34117-2',
              display: 'History and physical note',
              system: 'http://loinc.org',
            },
            {
              system: 'https://behuman.health/document-type',
              code: 'HistoryPhysical',
              display: 'History and Physical',
            },
          ],
          text: 'History and Physical',
        },
      }
    case 'Order':
      return {
        type: {
          coding: [
            {
              system: 'https://behuman.health/document-type',
              code: 'Order',
              display: 'Order',
            },
          ],
          text: 'Order',
        },
      }
    default:
      return {
        type: {
          coding: [
            {
              system: 'https://behuman.health/document-type',
              code: 'Others',
              display: 'Others',
            },
          ],
        },
      }
  }
}
const convertDateToISO = (dateStr: string) => {
  const [month, day, year] = dateStr.split('-')

  const date = new Date(`${year}-${month}-${day}T12:00:00Z`).toISOString()
  return date
}
const getDefaultDocDescription = (docType: string) => {
  switch (docType) {
    case 'progressNote':
      return 'Progress note'
    case 'HistoryPhysical':
      return 'History and physical'
    case 'Order':
      return 'Order'
    default:
      return 'Document'
  }
}

export async function uploadDocument(options: {
  docType?: string
  file: File // The PDF or TIF to upload
  title?: string //human title, e.g. "Progress note 2025-10-16"
  createdAt?: string //creation date of the PDF (optionally)
  patient?: Patient //Patient resource to use for display
  practitioner?: Practitioner //Practitioner resource to use for display
  related?: Reference<Resource>
  medplum?: MedplumClient //Medplum client;
}) {
  const { file, patient, practitioner, medplum, docType, createdAt, related } = options
  const patientId = patient?.id
  const practitionerId = practitioner?.id
  if (!medplum) {
    throw new Error('No Medplum client provided')
  }

  // Get current profile to resolve Practitioner and Organization
  const profile = medplum.getProfile()
  if (!profile) {
    throw new Error('No profile found; please sign in first')
  }

  // Get PractitionerRole to resolve Organization (if any)
  // Note: this assumes the current profile is a Practitioner or has a PractitionerRole
  // For more complex scenarios, pass in practitionerId and organizationId directly
  // via the options parameter

  const role = await medplum.searchOne(
    'PractitionerRole',
    `practitioner=Practitioner/${profile.id}&active=true`,
  )
  const organization = role?.organization?.reference
    ? await medplum.readReference(role.organization)
    : undefined

  if (
    !patientId ||
    organization?.resourceType !== 'Organization' ||
    !organization.id ||
    practitioner?.resourceType !== 'Practitioner' ||
    !practitioner.id ||
    !practitioner?.name ||
    !practitioner?.name[0]
  ) {
    throw new Error('No patientId, organization, or practitioner found')
  }

  // 1) Resolve author (Practitioner) from current profile if not provided
  let practitionerRef: string | undefined = practitionerId
    ? `Practitioner/${practitionerId}`
    : undefined

  if (!practitionerRef && profile?.resourceType === 'Practitioner' && profile.id) {
    practitionerRef = `Practitioner/${profile.id}`
  }
  if (!practitionerRef) {
    throw new Error(
      'No Practitioner context found. Pass practitionerId or sign in as a Practitioner.',
    )
  }

  // 2) Upload file as Binary
  const blobData = { data: file, contentType: file.type }
  const binary = await medplum.createBinary(blobData) // infers contentType

  // 3) Create DocumentReference

  const context = related ? { related: [related] } : undefined
  const docRefInput: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'final',
    subject: {
      reference: `Patient/${patientId}`,
      display: formatHumanName(patient?.name?.[0]),
    },
    date: createdAt ? convertDateToISO(createdAt) : new Date().toISOString(), // when the record was created in the system
    authenticator: { reference: practitionerRef, display: formatHumanName(practitioner?.name[0]) },
    description: options.title || file.name || getDefaultDocDescription(docType),
    securityLabel: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
            code: 'R',
            display: 'restricted',
          },
        ],
        text: 'restricted',
      },
    ],
    content: [
      {
        attachment: {
          contentType: file.type,
          url: `Binary/${binary.id}`,
          title: file.name ?? getDefaultDocDescription(options.title || ''),
        },
      },
    ],
    context,
    ...getDynamicPayload(docType),
  }

  const docRef = await medplum.createResource<DocumentReference>(docRefInput)

  // 4) Create Provenance that ties the DocumentReference to the Binary and the Practitioner
  const provenance = await createProvenanceResource(
    medplum,
    docRef.id,
    practitioner,
    organization,
    binary,
    'Progress Note',
  )

  return { binary, docRef, provenance }
}

export async function uploadOrderDocument(options: {
  file: File
  title?: string
  createdAt?: string
  patient?: Patient
  practitioner?: Practitioner
  formValues?: Record<string, unknown>
  medplum?: MedplumClient
}) {
  const { file, patient, practitioner, medplum, createdAt, formValues } = options
  const patientId = patient?.id
  if (!medplum) {
    throw new Error('No Medplum client provided')
  }
  if (!patientId) {
    throw new Error('No patientId found')
  }
  if (!practitioner?.id) {
    throw new Error('No practitioner found')
  }

  const authoredOn = createdAt ? convertDateToISO(createdAt) : new Date().toISOString()
  const orderTitle = options.title || getDefaultDocDescription('Order')
  const extensionSet = [
    ...(formValues?.orderDeliveryMethod
      ? ([buildDeliveryExtension(formValues.orderDeliveryMethod, formValues.faxNumber)].filter(
          Boolean,
        ) as DeliveryExtension[])
      : []),
    ...(formValues?.providerName
      ? [buildProviderNameExtension(formValues.providerName, formValues.providerReference || null)]
      : []),
  ]
  const requestGroupInput: RequestGroup = {
    resourceType: 'RequestGroup',
    status: 'active',
    intent: 'order',
    authoredOn,
    subject: {
      reference: `Patient/${patientId}`,
      display: formatHumanName(patient?.name?.[0]),
    },
    author: {
      reference: `Practitioner/${practitioner.id}`,
      display: formatHumanName(practitioner?.name?.[0]),
    },
    code: {
      text: orderTitle,
    },
    identifier: buildLabReferenceIdentifier(formValues?.labReferenceId || ''),
  }
  if (extensionSet.length > 0) {
    // @ts-expect-error extension not in base RequestGroup type
    requestGroupInput.extension = extensionSet
  }
  const requestGroup = await medplum.createResource<RequestGroup>(requestGroupInput)

  const binary = await medplum.createBinary({ data: file, contentType: file.type })

  const docRefInput: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'final',
    subject: {
      reference: `Patient/${patientId}`,
      display: formatHumanName(patient?.name?.[0]),
    },
    date: authoredOn,
    author: [
      {
        reference: `Practitioner/${practitioner.id}`,
        display: formatHumanName(practitioner?.name?.[0]),
      },
    ],
    description: orderTitle,
    content: [
      {
        attachment: {
          contentType: file.type,
          url: `Binary/${binary.id}`,
          title: file.name ?? orderTitle,
        },
      },
    ],
    context: {
      related: [{ reference: `RequestGroup/${requestGroup.id}` }],
    },
    ...getDynamicPayload('Order'),
  }

  const docRef = await medplum.createResource<DocumentReference>(docRefInput)

  return { requestGroup, binary, docRef }
}
// The org the report is filed for owns it: `resultsInterpreter` names it, and only that
// org's members may remove the report from the chart (PD-1460). Same rule as fax lab triage
// (resolveTaskOrgId in the middleware): the one org the patient and the practitioner share,
// else the patient's most recent org; null when the patient has no org.
async function resolveReportOwnerOrganizationId(
  medplum: MedplumClient,
  practitionerId: string | undefined,
  patient: Patient,
): Promise<string | null> {
  const orgIds = (refs: Reference[] | undefined): string[] =>
    (refs ?? [])
      .map((r) => r.reference)
      .filter((ref): ref is string => !!ref?.startsWith('Organization/'))
      .map((ref) => ref.split('/')[1])

  const accountOrgIds = orgIds(patient.meta?.accounts)
  const patientOrgIds = accountOrgIds.length > 0 ? accountOrgIds : orgIds(patient.meta?.compartment)
  if (patientOrgIds.length === 0) return null

  const roles = practitionerId
    ? await medplum.searchResources(
        'PractitionerRole',
        `practitioner=Practitioner/${practitionerId}&active=true`,
      )
    : []
  const practitionerOrgIds = new Set(orgIds(roles.map((r) => r.organization ?? {})))
  const shared = [...new Set(patientOrgIds)].filter((id) => practitionerOrgIds.has(id))
  return shared.length === 1 ? shared[0] : patientOrgIds[patientOrgIds.length - 1]
}

export async function uplaoadDiagnosticReport(options: {
  file: File // The PDF or TIF to upload
  title?: string //human title, e.g. "Progress note 2025-10-16"
  createdAt?: string //creation date of the PDF (optionally)
  patient?: Patient //Patient resource to use for display
  medplum?: MedplumClient //Medplum client;
  organizationName?: string
}) {
  const { file, patient, medplum, createdAt, organizationName } = options
  const diagnosticReportType = options?.title || 'Diagnostic Report'
  const patientId = patient?.id
  if (!medplum) {
    throw new Error('No Medplum client provided')
  }
  // Get current profile to resolve Practitioner and Organization
  const profile = medplum.getProfile()
  if (!profile) {
    throw new Error('No profile found; please sign in first')
  }
  const ownerOrganizationId =
    profile.resourceType === 'Practitioner' && patient
      ? await resolveReportOwnerOrganizationId(medplum, profile.id, patient)
      : null

  const blobData = { data: file, contentType: file.type }
  const binary = await medplum.createBinary(blobData) // infers contentType

  const docRefInput: DiagnosticReport = {
    resourceType: 'DiagnosticReport',
    status: 'final',
    subject: {
      reference: `Patient/${patientId}`,
      display: formatHumanName(patient?.name?.[0]),
    },
    effectiveDateTime: createdAt ? convertDateToISO(createdAt) : new Date().toISOString(), // when the record was created in the system
    performer: [
      {
        reference: `Organization/external-organization`,
        display: organizationName,
      },
    ],
    ...(ownerOrganizationId
      ? { resultsInterpreter: [{ reference: `Organization/${ownerOrganizationId}` }] }
      : {}),
    code: { text: diagnosticReportType || 'Diagnostic Report' },
    presentedForm: [
      {
        contentType: file.type,
        url: `Binary/${binary.id}`,
        title: file.name ?? getDefaultDocDescription(options.title || ''),
      },
    ],
  }
  const docRef = await medplum.createResource<DiagnosticReport>(docRefInput)
  return { binary, docRef }
}
